/**
 * Boundary-zod helpers.
 *
 * Cross-cutting recommendation #2 from the bug-hunt doc: every external
 * response should run through a typed schema, so shape drift becomes a
 * typed error at the API layer rather than a silent empty list or a
 * `TypeError: Cannot read 'x' of undefined` deep in the UI.
 *
 * Two helpers:
 *   - `parseResponse(schema, data)` — single-shot validation; throws
 *     `ResponseShapeError` on mismatch with a clean message.
 *   - `withSchema(schema)` — returns an axios `transformResponse`-style
 *     function suitable for `apiClient.get<T>(url, { ..., transformResponse: [...]})`,
 *     or for use in a `useQuery`'s `select`.
 *
 * Why a typed error instead of letting zod's own throw bubble up?
 *   - `ResponseShapeError` carries the field path so toast/dialog code
 *     can show "Backend returned malformed `results` field" rather than
 *     a generic stack-shaped string.
 *   - It's easy to grep / handle in error boundaries (`err instanceof
 *     ResponseShapeError` is the contract).
 */

import { type ZodError, type ZodSchema } from 'zod';

export class ResponseShapeError extends Error {
  constructor(
    message: string,
    public readonly issues: ZodError['issues']
  ) {
    super(message);
    this.name = 'ResponseShapeError';
  }
}

function formatIssue(issue: ZodError['issues'][number]): string {
  const path = issue.path.length > 0 ? issue.path.join('.') : '(root)';
  return `${path}: ${issue.message}`;
}

/**
 * Validate `data` against `schema`. Returns the parsed (and possibly
 * narrowed/coerced) value on success; throws ResponseShapeError on
 * failure with the first issue baked into the message.
 */
export function parseResponse<T>(schema: ZodSchema<T>, data: unknown): T {
  const result = schema.safeParse(data);
  if (result.success) {
    return result.data;
  }

  const first = result.error.issues[0];
  const summary = first ? formatIssue(first) : 'unknown shape mismatch';
  const more = result.error.issues.length > 1
    ? ` (+${result.error.issues.length - 1} more issue${result.error.issues.length > 2 ? 's' : ''})`
    : '';
  throw new ResponseShapeError(
    `Backend response failed schema validation: ${summary}${more}`,
    result.error.issues
  );
}

/**
 * Returns a function suitable as `useQuery({ select })`. Use it in
 * hooks where you want the response validated as it leaves the
 * query layer:
 *
 *   const InvitationList = z.object({ results: z.array(InvitationSchema) });
 *   useQuery({
 *     queryKey: invitationKeys.list(),
 *     queryFn: () => apiClient.get<unknown>('/api/invitations/').then(r => r.data),
 *     select: withSchema(InvitationList),
 *   });
 */
export function withSchema<T>(schema: ZodSchema<T>): (data: unknown) => T {
  return (data) => parseResponse(schema, data);
}
